import { useState } from "react";
import { Settings, defaultData } from "./AppTypes";
import { getBaseColor, Mode } from "./colorUtils";
import { notifyProjectActivity } from "./api/pingClient";

// Muss mit backend/settings.py SPECIAL_CHARACTERS synchron bleiben
const SPECIAL_CHARACTERS = ["·", ";", "\n", "|"];
const stripSpecialChars = (v: string) =>
  SPECIAL_CHARACTERS.reduce((s, c) => s.split(c).join(""), v);

export default function SettingEditor({
  settings,
  onChange,
  isActive,
  toggle,
  mode,
  projectId,
  projectName,
  organizationName,
  onProjectNameChange,
  onProjectNameCommit,
  onOrganizationNameChange,
  onOrganizationNameCommit,
}: {
  settings: Settings;
  onChange: (s: Settings) => void;
  isActive: boolean;
  toggle: () => void;
  mode: Mode;
  projectId: number;
  projectName: string;
  organizationName: string;
  onProjectNameChange: (newName: string) => void;
  onProjectNameCommit: (newName: string) => void;
  onOrganizationNameChange: (newName: string) => void;
  onOrganizationNameCommit: (newName: string) => void;
}) {
  const [showProjectNameWarning, setShowProjectNameWarning] = useState<string | null>(null);
  const [showOrgNameWarning, setShowOrgNameWarning] = useState<string | null>(null);

  const handleChange = (patch: Partial<Settings>) => {
    // Settings-Änderung an den Parent weitergeben
    onChange({ ...settings, ...patch });

    // Aktivitäts-Ping ans Backend (fire-and-forget)
    notifyProjectActivity(projectId).catch(() => {
      // optional: console.debug("activity ping failed");
    });
  };

  return (
    <div
      className="settings-panel"
      style={{
        background: getBaseColor(
          2,
          "section",
          settings.hue ?? defaultData.settings.hue,
          mode
        ),
      }}
    >
      {/* Header */}
      <div
        className="settings-panel__header"
        onClick={toggle}
      >
        <div className="settings-panel__title">
          {isActive ? "▾" : "▸"} Settings
        </div>
      </div>


      {isActive && (
        <div className="settings-panel__content">
          {/* DB-basierte Namen (Projekt & Organisation) */}
          {/*
              Live-Update-Logik:
              1. `value` und `onChange` sorgen dafür, dass sich der State im Parent (App.tsx)
                 sofort ändert. Dadurch wird der Header oben (Plan.tsx) live beim Tippen aktualisiert.
              2. `onBlur` feuert erst, wenn das Feld verlassen wird. Hier rufen wir die API auf,
                 um die Änderung persistent in der Datenbank zu speichern.
           */}
          <label>
            ProjectName:{" "}
            <input
              type="text"
              value={projectName}
              onChange={(e) => {
                const raw = e.target.value;
                const stripped = stripSpecialChars(raw);
                if (stripped !== raw) {
                  const found = SPECIAL_CHARACTERS
                    .filter(c => raw.includes(c))
                    .map(c => c === "\n" ? "\u21b5" : c)
                    .join(", ");
                  setShowProjectNameWarning(found);
                  setTimeout(() => setShowProjectNameWarning(null), 2000);
                }
                onProjectNameChange(stripped);
              }}
              onBlur={(e) => onProjectNameCommit(stripSpecialChars(e.target.value))}
            />
            {showProjectNameWarning && (
              <span style={{ color: "#e55", fontSize: "0.8em", marginLeft: 4 }}>
                "{showProjectNameWarning}" nicht erlaubt
              </span>
            )}
          </label>

          <label>
            OrganizationName:{" "}
            <input
              type="text"
              value={organizationName}
              onChange={(e) => {
                const raw = e.target.value;
                const stripped = stripSpecialChars(raw);
                if (stripped !== raw) {
                  const found = SPECIAL_CHARACTERS
                    .filter(c => raw.includes(c))
                    .map(c => c === "\n" ? "\u21b5" : c)
                    .join(", ");
                  setShowOrgNameWarning(found);
                  setTimeout(() => setShowOrgNameWarning(null), 2000);
                }
                onOrganizationNameChange(stripped);
              }}
              onBlur={(e) => onOrganizationNameCommit(stripSpecialChars(e.target.value))}
            />
            {showOrgNameWarning && (
              <span style={{ color: "#e55", fontSize: "0.8em", marginLeft: 4 }}>
                "{showOrgNameWarning}" nicht erlaubt
              </span>
            )}
          </label>

          <hr />

          {/* Restliche XML-Settings wie bisher, aber ohne den alten ProjectName-Eingabefeld */}
          <div>zeitgeist: 0.12</div>
          <div>Rules: {settings.version}</div>

          <div>
            Hue (0–359):{" "}
            <input
              type="number"
              min={0}
              max={359}
              value={settings.hue ?? defaultData.settings.hue}
              onChange={(e) =>
                handleChange({ hue: Number(e.target.value) })
              }
            />
          </div>

          <div>
            Language:{" "}
            <select
              value={settings.language}
              onChange={(e) =>
                handleChange({ language: e.target.value })
              }
            >
              <option value="de">de</option>
              <option value="en">en</option>
            </select>
          </div>

          <div>
            Region:{" "}
            <select
              value={settings.region}
              onChange={(e) =>
                handleChange({ region: e.target.value })
              }
            >
              <option value="DE">DE</option>
            </select>
          </div>

          <div>
            State:{" "}
            <select
              value={settings.state}
              onChange={(e) =>
                handleChange({ state: e.target.value })
              }
            >
              <option value="NI">NI</option>
              <option value="BY">BY</option>
              <option value="NW">NW</option>
              {/* weitere Bundesländer */}
            </select>
          </div>

          <div>
            Calendar:{" "}
            <select
              value={settings.calendar}
              onChange={(e) =>
                handleChange({ calendar: e.target.value })
              }
            >
              <option value="iso8601">iso8601</option>
            </select>
          </div>

          <div>
            Timezone:{" "}
            <select
              value={settings.timezone}
              onChange={(e) =>
                handleChange({ ...settings, timezone: e.target.value })
              }
            >
              <option value="Europe/Berlin">Europe/Berlin</option>
            </select>
          </div>
        </div>
      )}
    </div>
  );
}